import { create } from "zustand"
import api from "../services/api"

// Mesmo fallback do ListService da API: COALESCE(ordem, 999) — itens sem ordem vão ao final.
const N_SITUACAO_ORDEM_PADRAO_FINAL = 999

const situacaoPaginadaChaveGerar = (cBusca = "", nPagina = 1) => `${cBusca}|${nPagina}`

const situacaoOrdemObter = (oSituacao) => oSituacao.ordem ?? N_SITUACAO_ORDEM_PADRAO_FINAL

const situacoesOrdenarPorOrdem = (mSituacoes) =>
  [...mSituacoes].sort((oA, oB) => situacaoOrdemObter(oA) - situacaoOrdemObter(oB))

const situacaoCorrespondeBusca = (cBusca, oSituacao) => {
  if (!cBusca) return true
  const cTermo = cBusca.toLowerCase()
  const cNome = (oSituacao.name || "").toLowerCase()
  const cCor = (oSituacao.color || "").toLowerCase()
  return cNome.includes(cTermo) || cCor.includes(cTermo)
}

const situacaoAtualizarNaLista = (mLista, oSituacao) => {
  const nIndice = mLista.findIndex((oItem) => oItem.id === oSituacao.id)
  if (nIndice === -1) {
    return situacoesOrdenarPorOrdem([...mLista, { ticketsCount: 0, ...oSituacao }])
  }
  const mNova = [...mLista]
  mNova[nIndice] = {
    ...mNova[nIndice],
    ...oSituacao,
    ticketsCount: oSituacao.ticketsCount ?? mNova[nIndice].ticketsCount ?? 0,
  }
  return situacoesOrdenarPorOrdem(mNova)
}

const situacaoRemoverDaLista = (mLista, nSituacaoId) => mLista.filter((oItem) => oItem.id !== nSituacaoId)

const situacaoListaSimplesAplicarEvento = (mLista, cAcao, oSituacao, nSituacaoId) => {
  if ((cAcao === "create" || cAcao === "update") && oSituacao) {
    return situacaoAtualizarNaLista(mLista, oSituacao)
  }
  if (cAcao === "delete") {
    return situacaoRemoverDaLista(mLista, Number(nSituacaoId))
  }
  return mLista
}

const situacaoListaPaginadaAplicarEvento = (mSituacoes, cBusca, cAcao, oSituacao, nSituacaoId) => {
  if (cAcao === "create" && oSituacao && situacaoCorrespondeBusca(cBusca, oSituacao)) {
    return situacaoAtualizarNaLista(mSituacoes, oSituacao)
  }
  if (cAcao === "update" && oSituacao) {
    return situacaoCorrespondeBusca(cBusca, oSituacao)
      ? situacaoAtualizarNaLista(mSituacoes, oSituacao)
      : situacaoRemoverDaLista(mSituacoes, oSituacao.id)
  }
  if (cAcao === "delete") {
    return situacaoRemoverDaLista(mSituacoes, Number(nSituacaoId))
  }
  return mSituacoes
}

const situacaoPaginadoPorChaveAplicarEvento = (oPaginadoPorChave, cAcao, oSituacao, nSituacaoId) =>
  Object.fromEntries(
    Object.entries(oPaginadoPorChave).map(([cChave, oEntrada]) => {
      if (!oEntrada?.situacoes) return [cChave, oEntrada]
      const [cBusca] = cChave.split("|")
      const mSituacoes = situacaoListaPaginadaAplicarEvento(
        oEntrada.situacoes,
        cBusca,
        cAcao,
        oSituacao,
        nSituacaoId
      )
      return mSituacoes === oEntrada.situacoes
        ? [cChave, oEntrada]
        : [cChave, { ...oEntrada, situacoes: mSituacoes }]
    })
  )

export const useSituacaoStore = create((set, get) => ({
  nGeracaoCache: 0,
  mListaSimples: [],
  oPaginadoPorChave: {},
  lPaginadoInvalido: true,
  lListaSimplesInvalida: true,
  oBuscaListaSimplesPendente: null,
  oBuscaPaginadaPendentePorChave: {},

  reiniciar: () =>
    set((oEstado) => ({
      nGeracaoCache: oEstado.nGeracaoCache + 1,
      mListaSimples: [],
      oPaginadoPorChave: {},
      lPaginadoInvalido: true,
      lListaSimplesInvalida: true,
      oBuscaListaSimplesPendente: null,
      oBuscaPaginadaPendentePorChave: {},
    })),

  invalidarTudo: () =>
    set((oEstado) => ({
      nGeracaoCache: oEstado.nGeracaoCache + 1,
      mListaSimples: [],
      oPaginadoPorChave: {},
      lPaginadoInvalido: true,
      lListaSimplesInvalida: true,
      oBuscaListaSimplesPendente: null,
      oBuscaPaginadaPendentePorChave: {},
    })),

  listaSimplesBuscar: async ({ lForcar = false } = {}) => {
    const oEstado = get()
    if (!lForcar && !oEstado.lListaSimplesInvalida) {
      return oEstado.mListaSimples
    }

    if (oEstado.oBuscaListaSimplesPendente) {
      return oEstado.oBuscaListaSimplesPendente
    }

    const nGeracao = oEstado.nGeracaoCache
    const oPromessa = api
      .get("/situacoes/list")
      .then(({ data }) => {
        if (get().nGeracaoCache !== nGeracao) {
          return []
        }
        const mLista = data || []
        set({ mListaSimples: mLista, lListaSimplesInvalida: false, oBuscaListaSimplesPendente: null })
        return mLista
      })
      .catch((oErro) => {
        if (get().nGeracaoCache === nGeracao) {
          set({ oBuscaListaSimplesPendente: null })
        }
        throw oErro
      })

    set({ oBuscaListaSimplesPendente: oPromessa })
    return oPromessa
  },

  listaSimplesObter: async (oOpcoes) => get().listaSimplesBuscar(oOpcoes),

  paginadaBuscar: async ({ searchParam: cBusca = "", pageNumber: nPagina = 1, force: lForcar = false } = {}) => {
    const cChave = situacaoPaginadaChaveGerar(cBusca, nPagina)
    const oEstado = get()

    if (!lForcar && !oEstado.lPaginadoInvalido && oEstado.oPaginadoPorChave[cChave]) {
      return oEstado.oPaginadoPorChave[cChave]
    }

    if (oEstado.oBuscaPaginadaPendentePorChave[cChave]) {
      return oEstado.oBuscaPaginadaPendentePorChave[cChave]
    }

    const nGeracao = oEstado.nGeracaoCache
    const oPromessa = api
      .get("/situacoes/", { params: { searchParam: cBusca, pageNumber: nPagina } })
      .then(({ data }) => {
        if (get().nGeracaoCache !== nGeracao) {
          return { situacoes: [], hasMore: false, count: 0 }
        }
        const oPayload = {
          situacoes: data.situacoes || [],
          hasMore: data.hasMore,
          count: data.count,
        }
        set((oAtual) => ({
          oPaginadoPorChave: { ...oAtual.oPaginadoPorChave, [cChave]: oPayload },
          lPaginadoInvalido: false,
          oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: undefined },
        }))
        return oPayload
      })
      .catch((oErro) => {
        if (get().nGeracaoCache === nGeracao) {
          set((oAtual) => ({
            oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: undefined },
          }))
        }
        throw oErro
      })

    set((oAtual) => ({
      oBuscaPaginadaPendentePorChave: { ...oAtual.oBuscaPaginadaPendentePorChave, [cChave]: oPromessa },
    }))
    return oPromessa
  },

  paginadaObter: async (oParametros) => get().paginadaBuscar(oParametros),

  eventoSocketAplicar: (oDados) => {
    const { action: cAcao, situacao: oSituacao, situacaoId: nSituacaoId } = oDados || {}

    set((oEstado) => ({
      mListaSimples: situacaoListaSimplesAplicarEvento(
        oEstado.mListaSimples,
        cAcao,
        oSituacao,
        nSituacaoId
      ),
      oPaginadoPorChave: situacaoPaginadoPorChaveAplicarEvento(
        oEstado.oPaginadoPorChave,
        cAcao,
        oSituacao,
        nSituacaoId
      ),
      lListaSimplesInvalida: oEstado.lListaSimplesInvalida,
      lPaginadoInvalido: oEstado.lPaginadoInvalido,
    }))
  },
}))

export { situacaoPaginadaChaveGerar, situacoesOrdenarPorOrdem }
